import React from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { addUrlProtocol } from '@Utils/String';
import Catalyst from '@Services/Catalyst';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import { getOfferIdentifier } from './SubstanceCommercialSupplierInfo.helpers';
import AggregatorPreviewTable from './SubstanceCommercialSupplierAggregatorPreviewTable';
import NormalSupplierOffersTable from './SubstanceCommercialSupplierNormalOffersTable';

import './SubstanceCommercialSupplierDetailRow.scss';

// Normal suppliers always show every column, even when values are empty.
const ALL_NORMAL_DETAIL_COLUMNS = {
  showPurity: true,
  showPackageSize: true,
  showPrice: true,
  showShipmentCountry: true,
  showShipping: true,
  showAvailability: true,
};

function getSupplierAnalyticsContent(summaryOffer = {}, casRegistryNumbers = '') {
  return {
    id: casRegistryNumbers,
    id2: summaryOffer.suppliersProductNumber,
    id3: summaryOffer.rcProductName,
  };
}

function SupplierInfoSection({ casRegistryNumbers = '', group }) {
  const summaryOffer = group?.summaryOffer || {};
  const { supplierLocation = '', supplierName = '', supplierUrl = '', lastUpdated = '' } = group;
  const openWebsite = () => {
    if (!supplierUrl) return;
    const url = addUrlProtocol(supplierUrl);

    Catalyst.trackLinkOut(
      {
        ...getSupplierAnalyticsContent(summaryOffer, casRegistryNumbers),
        linkOut: 'supplier-info:supplier-name',
      },
      {
        location: url,
        name: supplierName,
        type: 'Supplier Information',
        destination: supplierName,
      }
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rcs-commercial-supplier-info">
      <div className="rcs-commercial-supplier-info__items">
        {supplierLocation && (
          <dl className="rcs-commercial-supplier-info__item" role="presentation">
            <dt className="rcs-commercial-supplier-info__label">Location</dt>
            <dd className="rcs-commercial-supplier-info__value">{supplierLocation}</dd>
          </dl>
        )}
        {supplierUrl && (
          <div className="rcs-commercial-supplier-info__item">
            <ButtonWithIcon
              data-e2e="commercial-supplier-website-button"
              type="link"
              sprite="UpRight"
              iconRight
              linkIconSize="xs"
              className="rcs-commercial-supplier-info__link"
              onClick={openWebsite}>
              Website
            </ButtonWithIcon>
          </div>
        )}
        {lastUpdated && (
          <dl className="rcs-commercial-supplier-info__item rcs-commercial-supplier-info__item--last-updated" role="presentation">
            <dt className="rcs-commercial-supplier-info__label">Last updated:</dt>
            <dd className="rcs-commercial-supplier-info__value">{lastUpdated}</dd>
          </dl>
        )}
      </div>
    </div>
  );
}

SupplierInfoSection.propTypes = {
  casRegistryNumbers: PropTypes.string,
  group: PropTypes.object.isRequired,
};

function ProductInformationSection({ casRegistryNumbers = '', group, onOpenModal }) {
  const summaryOffer = group?.summaryOffer || {};
  const identificationNumber = getOfferIdentifier(summaryOffer);
  const analyticsContent = getSupplierAnalyticsContent(summaryOffer, casRegistryNumbers);
  const analyticsLink = {
    type: 'Supplier Information',
    destination: group.supplierName,
  };
  const openProductLink = () => {
    if (!summaryOffer?.productUrl) return;
    const url = addUrlProtocol(summaryOffer.productUrl);

    if (summaryOffer.suppliersProductNumber) {
      Catalyst.trackLinkOut(
        {
          ...analyticsContent,
          linkOut: 'supplier-info:supplier-product-number',
        },
        {
          ...analyticsLink,
          location: url,
          name: summaryOffer.suppliersProductNumber,
        }
      );
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const openSafetyDataSheets = () => {
    if (!summaryOffer?.sdsUrl) return;
    const url = addUrlProtocol(summaryOffer.sdsUrl);

    Catalyst.trackLinkOut(
      {
        ...analyticsContent,
        linkOut: 'supplier-info:sds',
      },
      {
        ...analyticsLink,
        location: url,
        name: 'SDS',
      }
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rcs-commercial-product-info">
      <div className="rcs-commercial-product-info__header">
        {group.isAggregator ? (
          <ButtonWithIcon
            data-e2e={`commercial-supplier-product-information-${group.supplierId}`}
            type="link"
            sprite="ChevronRight"
            iconRight
            linkIconSize="xs"
            className="rcs-commercial-product-info__trigger"
            onClick={onOpenModal}>
            Product information
          </ButtonWithIcon>
        ) : (
          <div className="rcs-commercial-product-info__details">
            {identificationNumber && (
              <dl className="rcs-commercial-product-info__item" role="presentation">
                <dt className="rcs-commercial-product-info__label">Identification number:</dt>
                <dd className="rcs-commercial-product-info__value">{identificationNumber}</dd>
              </dl>
            )}
            {summaryOffer?.rcProductName && (
              <dl className="rcs-commercial-product-info__item" role="presentation">
                <dt className="rcs-commercial-product-info__label">Product name</dt>
                <dd className="rcs-commercial-product-info__value">{summaryOffer.rcProductName}</dd>
              </dl>
            )}
            {summaryOffer?.sdsUrl && (
              <ButtonWithIcon
                data-e2e="commercial-supplier-safety-data-sheets"
                type="link"
                sprite="UpRight"
                iconRight
                linkIconSize="xs"
                className="rcs-commercial-product-info__sds-link"
                onClick={openSafetyDataSheets}>
                Safety data sheets
              </ButtonWithIcon>
            )}
          </div>
        )}

        {summaryOffer?.productUrl && (
          <ButtonWithIcon
            data-e2e="commercial-supplier-view-product"
            type="secondary"
            size="small"
            sprite="UpRight"
            iconRight
            linkIconSize="xs"
            onClick={openProductLink}>
            View product
          </ButtonWithIcon>
        )}
      </div>
    </div>
  );
}

ProductInformationSection.propTypes = {
  casRegistryNumbers: PropTypes.string,
  group: PropTypes.object.isRequired,
  onOpenModal: PropTypes.func.isRequired,
};

function SubstanceCommercialSupplierDetailRow({
  casRegistryNumbers = '',
  group,
  detailRowId,
  isProdSelectionEnabled = false,
  onOpenAggregatorModal,
  substanceId,
  substanceIndex,
  totalColumns,
}) {
  const supplierName = sanitizeDiscard(String(group.supplierName || ''), { allowedTags: [] });

  return (
    <tr id={detailRowId} className="rcs-commercial-detail-row no-hover-background">
      <td
        role="rowheader"
        aria-label={`Supplier and product information for ${supplierName}`}
        className="rcs-commercial-detail-row__cell"
        colSpan={totalColumns}>
        <div className="rcs-commercial-detail-panel">
          <div role="group" aria-label="Supplier and product information">
            <SupplierInfoSection casRegistryNumbers={casRegistryNumbers} group={group} />
            <ProductInformationSection casRegistryNumbers={casRegistryNumbers} group={group} onOpenModal={onOpenAggregatorModal} />
          </div>
          {group.isAggregator ? (
            <AggregatorPreviewTable group={group} />
          ) : (
            <NormalSupplierOffersTable
              columns={ALL_NORMAL_DETAIL_COLUMNS}
              isProdSelectionEnabled={isProdSelectionEnabled}
              offers={group.offers}
              substanceId={substanceId}
              substanceIndex={substanceIndex}
              tableId={detailRowId}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

SubstanceCommercialSupplierDetailRow.propTypes = {
  casRegistryNumbers: PropTypes.string,
  group: PropTypes.object.isRequired,
  detailRowId: PropTypes.string.isRequired,
  isProdSelectionEnabled: PropTypes.bool,
  onOpenAggregatorModal: PropTypes.func.isRequired,
  substanceId: PropTypes.string.isRequired,
  substanceIndex: PropTypes.number.isRequired,
  totalColumns: PropTypes.number.isRequired,
};

export default SubstanceCommercialSupplierDetailRow;
